import React, { useMemo, useState } from 'react';
import { TicketIcon, TrashIcon, ChevronUpIcon, ChevronDownIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';
import SelectInput from '../../pro/components/orderform/SelectInput';
import TextInput from '../../pro/components/orderform/TextInput';
import Card from './Card';
import type { TariffRateRow } from '../fixtures';
import quoteService, { type ChargeCatalogRow, type PublishedTariffRate } from '../../../services/quote.service';
import TariffRatesPopup from './TariffRatesPopup';
import AddChargesPopup, { type SelectedCharge } from './AddChargesPopup';
import { CHARGE_RATE_TYPES, CHASSIS_FEE_TYPES, FREE_TIME_UNITS, withCurrent } from '../quoteOptions';

interface Props {
  rows: TariffRateRow[];
  /** Optional caption shown under the "+ Add New" button when relevant —
   *  e.g. "Showing rates scoped to dray" so users know why a row that exists
   *  in the data isn't on this tab. */
  caption?: string;
  /** When provided (with the handlers below) the card becomes editable:
   *  rate-type/rate are live inputs, rows get a delete button, and "+ Add New"
   *  opens an inline catalog-driven add row. */
  editable?: boolean;
  /** Secured-quote "add-only" mode: adding a charge is allowed and rows added
   *  this session (negative temp id) stay editable/removable, but pre-existing
   *  (saved) rows — which carry a real recno (id >= 0) — are locked read-only.
   *  Requires `editable`. */
  addOnly?: boolean;
  catalog?: ChargeCatalogRow[];
  /** When set, the added charge is pinned to this child row (a deliveryquote
   *  lane recno) — forwarded as `tablerecno` in the onAdd payload. */
  tablerecno?: number;
  onAdd?: (payload: { additionalchargesrecno: number; addlchargeratetype: string; addlchargerate: number; freetime?: number; freetimeunit?: string; additionaltype?: string; additionaldays?: number; tablerecno?: number }) => void;
  onChangeRow?: (id: number, patch: { addlchargeratetype?: string; addlchargerate?: number; freetime?: number; freetimeunit?: string; additionaltype?: string; additionaldays?: number }) => void;
  onDeleteRow?: (id: number) => void;
  /** Optional per-row content for the trailing "Additional" column — used by
   *  the Direct tab to attach a charge's modifiers to its own line item: the
   *  chassis-fee Type dropdown (+ a Days input for MIN/MAX), or the waiting/yard
   *  free-time value. Returns null/undefined for rows with no modifier. */
  rowModifier?: (row: TariffRateRow) => React.ReactNode;
  /** Resolves the Free Time cell's value + unit for a row. Lets the Direct tab
   *  mirror WAITING TIME / YARD STORAGE from the V1 directquote field. Falls back
   *  to the charge's own freeTime/freeTimeUnit when absent. */
  freeTimeFor?: (row: TariffRateRow) => { value: string; unit: string };
  /** Writes a Free Time edit (value + unit). The Direct tab uses this to persist
   *  BOTH the charge's freetime AND the V1 directquote field. Falls back to
   *  onChangeRow({ freetime, freetimeunit }) when absent. */
  onFreeTimeChange?: (row: TariffRateRow, value: number, unit: string) => void;
  /** After a charge is added via the popup, persist its Additional/Free-Time
   *  "extras" to the V1 directquote fields (chassis Type/Days, waiting/yard free
   *  time). The Direct tab maps by description; other surfaces omit it. */
  onAddModifier?: (description: string, extras: { freetime?: number; freetimeunit?: string; chassisType?: string; chassisDays?: number }) => void;
}

/**
 * Dark-headed "Additional Charges" sub-table. Used on Dray / Direct /
 * (optionally) Warehouse / Delivery tabs. Each row of
 * quote.quoteadditionalcharges renders as one table row; the "Additional"
 * column hosts any per-charge modifier supplied via rowModifier (e.g. the
 * chassis-fee Type + Days controls); a headerless far-right column holds the
 * per-row delete (trash) button.
 *
 * The header carries a "Tariff Rates" link that opens the read-only published-
 * defaults popup (N61); the "+ Add New" catalog dropdown hides charges that are
 * already on the list.
 */
const TariffRatesCard: React.FC<Props> = ({
  rows, caption, editable = false, addOnly = false, catalog = [], tablerecno, onAdd, onChangeRow, onDeleteRow,
  rowModifier, freeTimeFor, onFreeTimeChange, onAddModifier,
}) => {
  const [multiOpen, setMultiOpen] = useState(false);

  // Client-side sort by Description / Rate Type / Rate (the "Additional" column
  // holds controls, not data, so it isn't sortable). null = the data's own
  // order. Sorting is display-only — row keys/ids are unchanged, so edit/delete
  // still target the right row.
  type SortKey = 'description' | 'rateType' | 'rate';
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' } | null>(null);
  const toggleSort = (key: SortKey) =>
    setSort((s) => (s && s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  const canEdit = editable && Boolean(onChangeRow);
  // Per-row editability. Normally this is the card-level `canEdit`; in add-only
  // mode (secured quote) only rows added this session — negative temp id — are
  // editable, so saved rows (id >= 0) render read-only.
  const rowEditable = (r: TariffRateRow) => canEdit && (!addOnly || r.id < 0);

  // "Tariff Rates" reference popup (published defaults, N61) — lazy-loaded on open.
  const [tariffOpen, setTariffOpen] = useState(false);
  const [tariffRows, setTariffRows] = useState<PublishedTariffRate[]>([]);
  const [tariffLoaded, setTariffLoaded] = useState(false);
  const openTariff = () => {
    setTariffOpen(true);
    if (tariffLoaded) return;
    quoteService.listTariffRates()
      .then((r) => { setTariffRows(r); setTariffLoaded(true); })
      .catch(() => { /* leave empty → popup shows "No tariff rates." */ });
  };

  // Visibility mirrors V1's "removed" state: a charge is soft-removed by blanking
  // its rate type (the delete endpoint sets addlchargeratetype='', and every
  // backend read filters TRIM(COALESCE(addlchargeratetype,''))<>''). So a row
  // shows whenever it HAS a rate type — INCLUDING at rate 0. A zero rate is a
  // real charge (a waived/zeroed fee), not a removal; only a blank type hides it.
  const isVisible = (r: TariffRateRow) => String(r.rateType ?? '').trim() !== '';

  // Don't offer charges already shown on this list (match by description — the
  // detail rows carry no catalog recno). A hidden (blank-rate-type) charge is
  // treated as NOT present, so it can be re-added (applyAdd sets the rate + type
  // on the existing hidden row rather than inserting a duplicate).
  const existingDescriptions = new Set(rows.filter(isVisible).map((r) => r.description));
  const addableCatalog = catalog.filter((c) => !existingDescriptions.has(c.description));

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const copy = [...rows];
    copy.sort((a, b) => {
      const cmp = sort.key === 'rate'
        ? (Number(a.rate) || 0) - (Number(b.rate) || 0)
        : String(a[sort.key] || '').localeCompare(String(b[sort.key] || ''));
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [rows, sort]);

  // Rows actually shown: charges with a blank rate type (V1-removed) are hidden.
  const visibleRows = useMemo(() => sortedRows.filter(isVisible), [sortedRows]); // eslint-disable-line react-hooks/exhaustive-deps

  // Add path for the multi-select popup. Re-adding a charge that already exists
  // hidden at rate 0 sets its rate instead of creating a duplicate row (matches
  // the blank-rate-type "not present" treatment above). Blank rate type falls
  // back to EXTRA (a blank type drops the charge from the list + the PDF).
  const applyAdd = (recno: number, ratetype: string, rate: number, freetime = 0, freetimeunit = '', additionaltype = '', additionaldays = 0) => {
    if (!onAdd || !recno) return;
    const type = ratetype || 'EXTRA';
    const extras = { additionaltype, additionaldays, ...(freetime > 0 ? { freetime, freetimeunit: freetimeunit || 'DAYS' } : {}) };
    const entry = catalog.find((c) => c.recno === recno);
    const existing = entry ? rows.find((r) => r.description === entry.description) : undefined;
    if (existing && onChangeRow) {
      onChangeRow(existing.id, { addlchargerate: rate, addlchargeratetype: type, ...extras });
    } else {
      onAdd({
        additionalchargesrecno: recno,
        addlchargeratetype: type,
        addlchargerate: rate,
        ...extras,
        ...(tablerecno != null ? { tablerecno } : {}),
      });
    }
  };

  const submitMulti = (entries: SelectedCharge[]) => {
    entries.forEach((e) => {
      applyAdd(e.recno, e.ratetype, e.rate, e.freetime, e.freetimeunit, e.chassisType, e.chassisDays);
      const desc = catalog.find((c) => c.recno === e.recno)?.description ?? '';
      onAddModifier?.(desc, { freetime: e.freetime, freetimeunit: e.freetimeunit, chassisType: e.chassisType, chassisDays: e.chassisDays });
    });
    setMultiOpen(false);
  };

  return (
    <Card
      icon={<TicketIcon className="h-3.5 w-3.5" />}
      title="Additional Charges"
      scaleKey="charges"
      collapsible={false}
      flushBody
      right={
        <div className="flex items-center gap-3">
          {caption && <span className="text-[0.6875rem] text-slate-500 dark:text-slate-400">{caption}</span>}
          <button
            type="button"
            onClick={openTariff}
            className="text-[0.71875rem] font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
          >
            Tariff Rates
          </button>
          <button
            type="button"
            onClick={editable && onAdd ? () => setMultiOpen(true) : undefined}
            disabled={!(editable && onAdd)}
            className="text-[0.71875rem] font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 disabled:text-slate-300 disabled:hover:text-slate-300 disabled:cursor-default dark:disabled:text-slate-600"
          >
            Add Additional Charges
          </button>
        </div>
      }
    >
      <div className="grid grid-cols-[1.4fr_1fr_1fr_1.4fr_1fr_2.5rem] bg-slate-50 border-b border-slate-200 dark:bg-slate-700/60 dark:border-slate-700">
        {([
          { label: 'Description', key: 'description' as SortKey },
          { label: 'Rate Type', key: 'rateType' as SortKey },
          { label: 'Rate', key: 'rate' as SortKey },
          { label: 'Additional' },
          { label: 'Free Time' },
          { label: '' }, // headerless delete column
        ]).map((c, i) => {
          const cellCls = `px-2.5 py-1.5 text-[0.65625rem] font-semibold text-slate-500 uppercase tracking-wider dark:text-slate-400 ${i < 5 ? 'border-r border-slate-200 dark:border-slate-700' : ''}`;
          if (!c.key) return <div key={c.label || 'delete'} className={cellCls}>{c.label}</div>;
          const active = sort?.key === c.key;
          // The WHOLE header cell is the sort toggle; the up/down chevron sits on
          // the right (justify-between). A faint up/down chevron marks sortable
          // columns that aren't the active sort.
          return (
            <button
              key={c.label}
              type="button"
              onClick={() => toggleSort(c.key!)}
              aria-label={`Sort by ${c.label}`}
              className={`${cellCls} flex items-center justify-between gap-1 text-left hover:text-slate-700 dark:hover:text-slate-200`}
            >
              <span>{c.label}</span>
              {active
                ? (sort!.dir === 'asc'
                    ? <ChevronUpIcon className="h-3 w-3 flex-shrink-0" />
                    : <ChevronDownIcon className="h-3 w-3 flex-shrink-0" />)
                : <ChevronUpDownIcon className="h-3 w-3 flex-shrink-0 text-slate-300 dark:text-slate-600" />}
            </button>
          );
        })}
      </div>
      {visibleRows.length === 0 ? (
        <div className="px-4 py-8 text-center text-slate-400 text-[0.78125rem] bg-white dark:bg-slate-800 dark:text-slate-500">
          <div className="mb-1 font-semibold text-slate-500 dark:text-slate-400">No additional charges yet</div>
          <div>
            {editable
              ? <>Click <strong className="text-blue-600 dark:text-blue-400">Add Additional Charges</strong> to add charges.</>
              : <>Save the quote, then click <strong className="text-blue-600 dark:text-blue-400">Add Additional Charges</strong> to add charges.</>}
          </div>
        </div>
      ) : (
        /* Each row wraps in its own `disabled` fieldset so read-only rows grey
           out their rate/type controls — this covers a fully-locked card AND
           the SAVED rows of a secured add-only card (where the card itself is
           interactive but individual saved rows are not). The sort header
           (above) + Tariff Rates link stay interactive because they're OUTSIDE
           these fieldsets. */
        <>
        {visibleRows.map((r, i) => {
          // #13: warn when the rate is below the catalog minimum. The detail row
          // carries no recno, so match the catalog by description (best-effort,
          // advisory) — mirrors the create wizard's below-min hint.
          const min = Number(catalog.find((c) => c.description === r.description)?.minimumRate) || 0;
          const belowMin = min > 0 && (Number(r.rate) || 0) > 0 && (Number(r.rate) || 0) < min;
          const modifier = rowModifier?.(r);
          const rEdit = rowEditable(r);
          return (
          <fieldset key={r.id} disabled={!rEdit} className="contents">
          <div
            className={`grid grid-cols-[1.4fr_1fr_1fr_1.4fr_1fr_2.5rem] items-center ${i < visibleRows.length - 1 ? 'border-b border-slate-100 dark:border-slate-700' : ''}`}
          >
            <div className="px-2.5 py-1.5 text-[0.75rem] font-semibold text-slate-900 border-r border-slate-100 dark:text-slate-100 dark:border-slate-700">
              {r.description || '—'}
              {r.pdfMessage && (
                <span className="ml-2 text-[0.65625rem] text-slate-400 font-normal italic dark:text-slate-500">{r.pdfMessage}</span>
              )}
            </div>
            <div className="px-1.5 py-1.5 border-r border-slate-100 dark:border-slate-700">
              {/* Exact "IN RATE" charges are baked into the quoted rate (they roll
                  into Total w/Charges); highlight the type so it stands out from
                  EXTRA. "EXTRA IN RATE" is a distinct type and stays plain. `!`
                  overrides baseSelect's own text color (Tailwind specificity). */}
              <SelectInput
                value={r.rateType}
                onChange={rEdit ? (v) => onChangeRow!(r.id, { addlchargeratetype: v }) : undefined}
                options={withCurrent(r.rateType, CHARGE_RATE_TYPES)}
                className={r.rateType === 'IN RATE' ? '!text-emerald-700 dark:!text-emerald-400 font-semibold' : undefined}
              />
            </div>
            <div className="px-1.5 py-1.5 border-r border-slate-100 dark:border-slate-700">
              <div className="flex items-center gap-2">
                {rEdit ? (
                  <input
                    defaultValue={r.rate}
                    aria-label="Rate"
                    onBlur={(e) => {
                      if (e.target.value !== r.rate) {
                        onChangeRow!(r.id, { addlchargerate: Number(e.target.value) || 0 });
                      }
                    }}
                    className={`w-28 border rounded px-1.5 py-1 text-[0.75rem] bg-white text-slate-900 dark:bg-gray-900 dark:text-slate-100 ${belowMin ? 'border-red-500 ring-1 ring-red-300 dark:border-red-500 dark:ring-red-800' : 'border-slate-300 dark:border-gray-600'}`}
                  />
                ) : (
                  <TextInput value={r.rate} width={112} />
                )}
                {/* Below-min warning sits to the RIGHT of the (now compact) input. */}
                {belowMin && <span className="text-[0.625rem] text-red-600 font-semibold whitespace-nowrap dark:text-red-400">min ${min}</span>}
              </div>
            </div>
            <div className="px-1.5 py-1.5 border-r border-slate-100 dark:border-slate-700 flex items-center gap-1.5">
              {modifier ?? <>
                <SelectInput value={r.additionalType || ''} options={['', ...CHASSIS_FEE_TYPES]} placeholder="—"
                  ariaLabel={`Additional type for ${r.description}`}
                  onChange={rEdit ? (v) => onChangeRow!(r.id, { additionaltype: v, additionaldays: ['MIN', 'MAX'].includes(v) ? (r.additionalDays ?? 0) : 0 }) : undefined} />
                {['MIN', 'MAX'].includes(r.additionalType || '') && <TextInput
                  value={String(r.additionalDays ?? 0)} width={44} ariaLabel={`Additional days for ${r.description}`}
                  onChange={rEdit ? (v) => onChangeRow!(r.id, { additionaldays: Number(v) || 0 }) : undefined} />}
              </>}
            </div>
            <div className="px-1.5 py-1.5 border-r border-slate-100 dark:border-slate-700">
              {(() => {
                // Value + unit come from freeTimeFor (Direct tab mirrors WAITING /
                // YARD from the directquote field); otherwise the charge's own.
                const ft = freeTimeFor ? freeTimeFor(r) : { value: r.freeTime ?? '', unit: r.freeTimeUnit || '' };
                const write = (value: number, unit: string) => {
                  if (onFreeTimeChange) onFreeTimeChange(r, value, unit);
                  else onChangeRow?.(r.id, { freetime: value, freetimeunit: value > 0 ? (unit || 'DAYS') : unit });
                };
                if (!rEdit) {
                  return (ft.unit && Number(ft.value) > 0) ? (
                    <span className="text-[0.75rem] text-slate-700 px-1.5 dark:text-slate-200">{ft.value} {ft.unit}</span>
                  ) : null;
                }
                return (
                  <div className="flex items-center gap-1.5">
                    <input
                      key={ft.value}
                      defaultValue={ft.value}
                      aria-label="Free time"
                      onBlur={(e) => {
                        const v = Number(e.target.value) || 0;
                        if (String(v) !== (ft.value ?? '')) write(v, ft.unit || 'DAYS');
                      }}
                      className="w-14 border rounded px-1.5 py-1 text-[0.75rem] bg-white text-slate-900 border-slate-300 dark:bg-gray-900 dark:text-slate-100 dark:border-gray-600"
                    />
                    <SelectInput
                      value={ft.unit || 'DAYS'}
                      onChange={(u) => write(Number(ft.value) || 0, u)}
                      options={FREE_TIME_UNITS}
                      ariaLabel="Free time unit"
                      width={72}
                    />
                  </div>
                );
              })()}
            </div>
            <div className="px-1.5 py-1.5 flex items-center justify-center">
              {rEdit && onDeleteRow && (
                <button
                  type="button"
                  onClick={() => onDeleteRow(r.id)}
                  aria-label="Remove charge"
                  className="w-5 h-5 inline-flex items-center justify-center text-slate-400 hover:text-red-600 dark:text-slate-500 dark:hover:text-red-400"
                >
                  <TrashIcon className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
          </fieldset>
          );
        })}
        </>
      )}
      {tariffOpen && <TariffRatesPopup rows={tariffRows} onClose={() => setTariffOpen(false)} />}
      {multiOpen && (
        <AddChargesPopup
          catalog={addableCatalog}
          onAdd={submitMulti}
          onClose={() => setMultiOpen(false)}
        />
      )}
    </Card>
  );
};

export default TariffRatesCard;
